import { useServerFn } from "@tanstack/react-start";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { deleteGrowthPlan, listGrowthPlans } from "@/lib/growth-plan.functions";
import { money } from "@/lib/growth-plan";

export const Route = createFileRoute("/_authenticated/admin/growth-plan/records")({
  component: RecordsPage,
});

function RecordsPage() {
  const listFn = useServerFn(listGrowthPlans);
  const delFn = useServerFn(deleteGrowthPlan);
  const [rows, setRows] = useState<any[] | null>(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      setRows((await listFn({})) as any[]);
    } catch (e: any) {
      setErr(e.message);
    }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        (r.agent_name ?? "").toLowerCase().includes(s) ||
        (r.current_brokerage ?? "").toLowerCase().includes(s),
    );
  }, [rows, q]);

  async function onDelete(id: string, name: string) {
    if (!confirm(`Delete the saved plan for ${name}?`)) return;
    await delFn({ data: { id } });
    load();
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <input
          placeholder="Search agent name or brokerage"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-72 border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
        />
        <Link to="/admin/growth-plan" search={{ id: undefined }} className="bg-primary px-5 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90">
          New plan
        </Link>
      </div>
      {err ? <p className="mb-4 text-primary">{err}</p> : null}
      {!filtered ? (
        <p className="text-foreground/65">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-foreground/65">No saved plans yet.</p>
      ) : (
        <div className="border border-border">
          <table className="w-full text-sm">
            <thead className="bg-surface text-xs uppercase tracking-[0.16em] text-foreground/65">
              <tr>
                <th className="p-3 text-left">Agent</th>
                <th className="p-3 text-left">Market</th>
                <th className="p-3 text-left">Track</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-right">Net delta</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3">
                    <Link to="/admin/growth-plan" search={{ id: r.id }} className="font-medium hover:text-primary">
                      {r.agent_name}
                    </Link>
                    <div className="text-xs text-foreground/55">{r.current_brokerage}</div>
                  </td>
                  <td data-label="Market" className="p-3 text-foreground/75">{r.market}</td>
                  <td data-label="Track" className="p-3 text-foreground/75">{r.track === "kcn_team" ? "KCN Team" : "Market Center"}</td>
                  <td data-label="Created" className="p-3 text-foreground/75">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td data-label="Net delta" className="p-3 text-right">{money(Number(r.net_delta))}</td>
                  <td className="p-3 text-right">
                    <Link to="/admin/growth-plan" search={{ id: r.id }} className="mr-4 text-xs uppercase tracking-[0.16em] text-foreground/75 hover:text-foreground">
                      Open
                    </Link>
                    <button onClick={() => onDelete(r.id, r.agent_name)} className="text-xs uppercase tracking-[0.16em] text-primary hover:underline">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
